import i18n from "@/i18n";
import { ApiEmployee, ApiEmployeeShort } from "@/types/employees/employee.types.ts";
import { ApiFile } from "@/types/common.types.ts";
import { ApiFilterBase } from "@skailer-com/uikit/core";
import { ReminderSettings, ReminderSettingsPayload } from "@/types/reminder.types.ts";
import { ApiWorkflowShort } from "@/types/workflows/workflow.types.ts";

export enum ApiTaskStatus {
  todo = "todo",
  in_progress = "in_progress",
  completed = "completed",
}

export enum ApiTaskEmployeeRole {
  assignee = "assignee",
  related = "related",
  watcher = "watcher",
}

export interface ApiTaskACL {
  can_edit: boolean;
  can_delete: boolean;
}

export interface ApiTask {
  id: number;
  title: string;
  description: string | null;
  date_due: Date | null;
  datetime_completed: Date | null;
  status: ApiTaskStatus;
  created_by: ApiEmployee | null;
  created_by_workflow: ApiWorkflowShort | null;
  assign_to_employee: ApiEmployee;
  visible_to_employees: ApiEmployee[];
  related_employee: ApiEmployee | null;
  files: ApiFile[];
  is_archived: boolean;
  acl: ApiTaskACL;
  datetime_last_reminder?: Date | null;
  reminder_settings: ReminderSettings | null;
}

export interface ApiTaskPayload {
  title: string;
  description: string | null;
  date_due: Date | null;
  assign_to_employee_id: number;
  related_employee_id: number | null;
  visible_to_employee_ids: number[];
  file_uuids: string[];
  reminder_settings: ReminderSettingsPayload | null;
}

export interface ApiTaskFilter extends ApiFilterBase {
  status?: ApiTaskStatus;
  is_archived?: boolean;
}

export type ApiTaskCommentFilter = ApiFilterBase;

export interface ApiTaskCommentPayload {
  text: string;
  file_uuids: string[] | null;
}

export enum ApiTaskActionType {
  created = "created",
  updated = "updated",
  completed = "completed",
  uncompleted = "uncompleted",
  started = "started",
  stopped = "stopped",
  archived = "archived",
}

export interface ApiTaskAction {
  action_type: ApiTaskActionType;
  datetime_created_at: Date;
  created_by: ApiEmployeeShort | null;
  created_by_workflow: ApiWorkflowShort | null;
  assign_to_employee: ApiEmployeeShort | null;
}

export const getTaskAction = (actionType: ApiTaskActionType): string => {
  const titles: Record<ApiTaskActionType, string> = {
    [ApiTaskActionType.created]: i18n.t("tasks:task_action_created"),
    [ApiTaskActionType.updated]: i18n.t("tasks:task_action_updated"),
    [ApiTaskActionType.completed]: i18n.t("tasks:task_action_completed"),
    [ApiTaskActionType.uncompleted]: i18n.t("tasks:task_action_uncompleted"),
    [ApiTaskActionType.started]: i18n.t("tasks:task_action_started"),
    [ApiTaskActionType.stopped]: i18n.t("tasks:task_action_stopped"),
    [ApiTaskActionType.archived]: i18n.t("tasks:task_action_archived"),
  };
  return titles[actionType];
};

export const getTaskWorkflowAction = (actionType: ApiTaskActionType): string => {
  const titles: Record<ApiTaskActionType, string> = {
    [ApiTaskActionType.created]: i18n.t("tasks:task_action_created_by_workflow"),
    [ApiTaskActionType.updated]: i18n.t("tasks:task_action_updated"),
    [ApiTaskActionType.completed]: i18n.t("tasks:task_action_completed"),
    [ApiTaskActionType.uncompleted]: i18n.t("tasks:task_action_uncompleted"),
    [ApiTaskActionType.started]: i18n.t("tasks:task_action_started"),
    [ApiTaskActionType.stopped]: i18n.t("tasks:task_action_stopped"),
    [ApiTaskActionType.archived]: i18n.t("tasks:task_action_archived"),
  };
  return titles[actionType];
};

export const getTaskStatus = (status: ApiTaskStatus): string => {
  const titles: Record<ApiTaskStatus, string> = {
    [ApiTaskStatus.todo]: i18n.t("tasks:task_status_todo"),
    [ApiTaskStatus.in_progress]: i18n.t("tasks:task_status_in_progress"),
    [ApiTaskStatus.completed]: i18n.t("tasks:task_status_completed"),
  };
  return titles[status];
};
